function startIndex(str: string, index: number, max: number) {
  do {
    const code = str.charCodeAt(index);
    if (code !== 0x20 /*   */ && code !== 0x09 /* \t */) return index;
  } while (++index < max);
  return max;
}

function endIndex(str: string, index: number, min: number) {
  while (index > min) {
    const code = str.charCodeAt(--index);
    if (code !== 0x20 /*   */ && code !== 0x09 /* \t */) return index + 1;
  }
  return min;
}
function decode(str: string): string {
  if (str.indexOf("%") === -1) return str;

  try {
    return decodeURIComponent(str);
  } catch {
    return str;
  }
}
function parseCookie(
  str: string,
  _options?: {decode: boolean}
): Record<string, string | undefined> {
  const obj: Record<string, string | undefined> = {};
  const len = str.length;
  // RFC 6265 sec 4.1.1, RFC 2616 2.2 defines a cookie name consists of one char minimum, plus '='.
  if (len < 2) return obj;

  let index = 0;

  do {
    const eqIdx = str.indexOf("=", index);
    if (eqIdx === -1) break; // No more cookie pairs.

    const colonIdx = str.indexOf(";", index);
    const endIdx = colonIdx === -1 ? len : colonIdx;

    if (eqIdx > endIdx) {
      // backtrack on prior semicolon
      index = str.lastIndexOf(";", eqIdx - 1) + 1;
      continue;
    }

    const keyStartIdx = startIndex(str, index, eqIdx);
    const keyEndIdx = endIndex(str, eqIdx, keyStartIdx);
    const key = str.slice(keyStartIdx, keyEndIdx);

    // only assign once
    if (obj[key] === undefined) {
      const valStartIdx = startIndex(str, eqIdx + 1, endIdx);
      const valEndIdx = endIndex(str, endIdx, valStartIdx);

      const value = decode(str.slice(valStartIdx, valEndIdx));
      obj[key] = value;
    }

    index = endIdx + 1;
  } while (index < len);

  return obj;
}

export function getAuthKeyFromCookie (cookie: string) {
  return parseCookie(cookie).auth_cookie
}

// One response can set auth_cookie twice: when the backend ends a revoked session (AB#65190), its
// authentication middleware deletes the cookie before the endpoint runs, and a sign-in on the same
// request then sets a new one. The deletion always comes first, so the last non-empty value is the
// new session. Only auth_cookie is repeated: the sign-out does not touch the other cookies.
export function getAuthKeyFromSetCookies (setCookies: string[]) {
  return setCookies.map(getAuthKeyFromCookie).filter(Boolean).pop()
}

export function getIsDemoFromCookie (cookie: string) {
  return parseCookie(cookie).isDemo
}
export function getIsAgentLoggedInFromCookie (cookie: string) {
  return parseCookie(cookie).isAgentLoggedIn
}
export function getIsAgentImpersonatedFromCookie (cookie: string) {
  return parseCookie(cookie).isAgentImpersonated
}
export function getAgentEmailFromCookie (cookie: string) {
  return parseCookie(cookie).agentEmail
}
export function getUserRoleFromCookie (cookie: string) {
  return parseCookie(cookie).userRole
}
// Parsed rather than `cookie.includes('isRecruit=true')`, which also matches
// `notisRecruit=true` and `isRecruit=trueX`. Raw value, like its neighbours. AB#64477
export function getIsRecruitFromCookie (cookie: string) {
  return parseCookie(cookie).isRecruit
}

// An agent's test recruit (join/agent-recruit), read from cookies: a recruit session with the
// agent signed in and impersonating. The client-side statement of the same rule is
// useSAuthState().isAgentRecruit; init-auth rebuilds its impersonation context from these
// cookies, which is what keeps the two in step. AB#64477
export function isAgentTestRecruitCookie (cookie: string) {
  return getIsRecruitFromCookie(cookie) === 'true'
    && getIsAgentLoggedInFromCookie(cookie) === 'true'
    && getIsAgentImpersonatedFromCookie(cookie) === 'true'
}

export function setRecruitCookie() {
  if (import.meta.client) {
    // Session cookie (no max-age/expires): the join guard is meant to trap a
    // recruit inside the funnel only for the current browsing session. A
    // persistent 24h cookie survived a browser restart and silently redirected
    // every off-join nav back to /join, so the header read as "unclickable" on
    // a fresh window. Dropping the lifetime clears the trap on browser close.
    // AB#61420.
    document.cookie = `isRecruit=true; path=/; secure; samesite=none`
  }
}

export function clearRecruitCookie() {
  if (import.meta.client) {
    document.cookie = 'isRecruit=; path=/; max-age=0; secure; samesite=none'
  }
}

// needed because $auth.$storage.getCookie decodeUriComponent for return value and appinsight ai data seems to be sometime malformed
export const getCookieValue = (name: string) => {
  // to be called by identify only in browser
  if (import.meta.client) {
    return document.cookie.match('(^|;)\\s*' + name + '\\s*=\\s*([^;]+)')?.pop() || ''
  }
}

// The Clarity custom tags for the App Insights session. One definition for the
// page-load and post-login sites, as nuxt2's aiData() was. AB#64457
export const aiData = (email?: string) => ({
  aiSession: getCookieValue('ai_session'),
  email
})
